import { ApiError, type IdTokenProvider } from './client'

// AuthorizeParams mirrors the eight fields handleAuthorize parses out of
// /oauth/authorize?... in cmd/console-api/oauth_authorize.go. The SPA
// reads them from the return_to query string and posts them straight
// back to /oauth/authorize/consent.
export interface AuthorizeParams {
  response_type: string
  client_id: string
  redirect_uri: string
  scope: string
  state: string
  code_challenge: string
  code_challenge_method: string
  resource: string
}

interface ConsentResponse {
  redirect_to: string
}

// postConsent posts to /oauth/authorize/consent. Distinct from apiFetch
// because the OAuth error envelope is {error, error_description} (RFC
// 6749 §5.2), not the standard {error:{code,message}}.
export async function postConsent(
  params: AuthorizeParams,
  approve: boolean,
  getIdToken: IdTokenProvider,
): Promise<ConsentResponse> {
  const token = await getIdToken()
  if (!token) {
    throw new ApiError(401, 'unauthenticated', 'not signed in')
  }

  let res: Response
  try {
    res = await fetch('/oauth/authorize/consent', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ ...params, approve }),
    })
  } catch (e) {
    throw new ApiError(0, 'network_error', e instanceof Error ? e.message : String(e))
  }

  let parsed: unknown = null
  const ct = res.headers.get('Content-Type') ?? ''
  if (ct.includes('application/json')) {
    try {
      parsed = await res.json()
    } catch {
      parsed = null
    }
  }

  if (!res.ok) {
    const code = readString(parsed, 'error') ?? 'http_error'
    const message = readString(parsed, 'error_description') ?? `request failed (${res.status})`
    throw new ApiError(res.status, code, message)
  }

  const redirect = readString(parsed, 'redirect_to')
  if (!redirect) {
    throw new ApiError(res.status, 'invalid_response', 'consent response missing redirect_to')
  }
  return { redirect_to: redirect }
}

// parseAuthorizeParams turns a return_to value like
// "/oauth/authorize?response_type=code&..." into the eight typed params.
// Returns null if the string isn't an /oauth/authorize URL or is missing
// any required field — the caller falls back to a normal post-signin
// navigation in that case.
export function parseAuthorizeParams(returnTo: string): AuthorizeParams | null {
  if (!returnTo.startsWith('/oauth/authorize?')) return null
  const q = returnTo.slice('/oauth/authorize?'.length)
  const params = new URLSearchParams(q)
  const out: AuthorizeParams = {
    response_type: params.get('response_type') ?? '',
    client_id: params.get('client_id') ?? '',
    redirect_uri: params.get('redirect_uri') ?? '',
    scope: params.get('scope') ?? '',
    state: params.get('state') ?? '',
    code_challenge: params.get('code_challenge') ?? '',
    code_challenge_method: params.get('code_challenge_method') ?? '',
    resource: params.get('resource') ?? '',
  }
  // Require everything except scope (which is optional per RFC 6749 §3.3
  // and defaulted server-side). Anything else missing means the server
  // would reject the consent post anyway, so don't show a stale consent
  // UI for an already-broken request.
  if (
    !out.response_type ||
    !out.client_id ||
    !out.redirect_uri ||
    !out.state ||
    !out.code_challenge ||
    !out.code_challenge_method ||
    !out.resource
  ) {
    return null
  }
  return out
}

function readString(parsed: unknown, key: string): string | null {
  if (typeof parsed !== 'object' || parsed === null) return null
  const v = (parsed as Record<string, unknown>)[key]
  return typeof v === 'string' ? v : null
}
